import React from 'react';
import { getJsonValueAtPath } from '@backstage/filter-predicates';
import type {
  FieldExtensionComponentProps,
  FieldSchema,
} from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  type FormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import { Flex, Text } from '@backstage/ui';

import { type DependsOnState, normalizeDependsOn } from './depends-on';
import { isJsonValue, type JsonValue } from './json-value';
import { useDependsOn } from './use-depends-on';

export interface FieldValueLabelUiOptions {
  dependsOn: string | string[];
  direction?: 'row' | 'column';
  /** Max 60 characters. Defaults to `'-'`. */
  emptyText?: string;
  /** Max 4 characters. Defaults to `', '`. */
  separator?: string;
  /** Max 256 characters. */
  valuePath: string;
}

export const fieldValueLabelSchema: FieldSchema<
  undefined,
  FieldValueLabelUiOptions
> = makeFieldSchema({
  output: z => z.undefined(),
  uiOptions: z =>
    z.object({
      dependsOn: z.union([z.string(), z.array(z.string())]),
      direction: z.enum(['row', 'column']).optional(),
      emptyText: z.string().max(60).optional(),
      separator: z.string().max(4).optional(),
      valuePath: z.string().max(256),
    }),
});

const DEFAULT_SEPARATOR = ', ';
const DEFAULT_EMPTY_TEXT = '-';
const DEFAULT_DIRECTION = 'row';

/**
 * Formats a resolved value per B-weergave. `items` holds one entry per
 * primitive in an array value (unjoined — the caller decides whether to join
 * them, per `direction`), or a single entry for a plain text/number/boolean
 * value or `emptyText`. Anything not found (or that cannot be shown on one
 * line — an object, or an array containing one) becomes a single-entry
 * `items` of `emptyText`. `warn` is true only for the "cannot be shown" case,
 * so the caller can log it once without conflating it with a merely-absent
 * value.
 */
function formatValue(
  value: JsonValue | undefined,
  emptyText: string,
): { items: string[]; warn: boolean } {
  if (value === undefined || value === null) {
    return { items: [emptyText], warn: false };
  }
  if (typeof value === 'string') {
    // Exactly '' counts as empty; a whitespace-only string is still text
    // (the filler typed something, even if it is invisible).
    return value.length === 0
      ? { items: [emptyText], warn: false }
      : { items: [value], warn: false };
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return { items: [String(value)], warn: false };
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return { items: [emptyText], warn: false };
    const isPrimitiveList = value.every(
      item => typeof item !== 'object' || item === null,
    );
    if (!isPrimitiveList) return { items: [emptyText], warn: true };
    return {
      items: value.map(item => (item === null ? '' : String(item))),
      warn: false,
    };
  }
  return { items: [emptyText], warn: true };
}

/** Reads a plain-string schema field (`title`/`description`), which arrives
 * as `JsonObject` and so is not statically known to be a string. */
function stringSchemaField(
  schema: FieldExtensionComponentProps<
    undefined,
    FieldValueLabelUiOptions
  >['schema'],
  field: 'description' | 'title',
): string | undefined {
  const value = schema[field];
  return typeof value === 'string' ? value : undefined;
}

/**
 * Resolves the `dependsOn` value (when ready) down to the display items for
 * `renderBody` below. Warns once, at the caller, when the resolved value
 * cannot be shown on one line.
 */
function resolveItems(
  state: DependsOnState,
  dependsOn: string | string[] | undefined,
  valuePath: string,
  emptyText: string,
): { items: string[]; warn: boolean } {
  if (state.kind !== 'ready') return { items: [emptyText], warn: false };
  const firstDependency = normalizeDependsOn(dependsOn)[0];
  const rawValue =
    firstDependency === undefined ? undefined : state.values[firstDependency];
  const rawValueJson = isJsonValue(rawValue) ? rawValue : undefined;
  const resolved = getJsonValueAtPath(rawValueJson, valuePath);
  return formatValue(resolved, emptyText);
}

/**
 * A stable key per item, since values can repeat: the value with its
 * occurrence number among equal values (`range-one#1`, `range-one#2`). The number
 * holds no `#`, so the last `#` always separates value and number and no
 * two keys can meet, whatever the values contain.
 */
function keyedItems(items: readonly string[]): { item: string; key: string }[] {
  const seen = new Map<string, number>();
  return items.map(item => {
    const occurrence = (seen.get(item) ?? 0) + 1;
    seen.set(item, occurrence);
    return { item, key: `${item}#${occurrence}` };
  });
}

/**
 * Renders the resolved items per B-weergave `direction`: `row` joins every
 * item into one line; `column` shows each item as its own element, stacked.
 * A single item (a plain value, or `emptyText`) is one element either way —
 * direction only matters once there is more than one item to arrange.
 */
function renderBody(
  items: string[],
  direction: 'column' | 'row',
  separator: string,
): React.ReactElement {
  if (direction === 'column') {
    return (
      <Flex direction="column">
        {keyedItems(items).map(({ item, key }) => (
          <Text key={key}>{item}</Text>
        ))}
      </Flex>
    );
  }
  return (
    <Flex direction="row">
      <Text>{items.join(separator)}</Text>
    </Flex>
  );
}

export function FieldValueLabelComponent(
  props: FieldExtensionComponentProps<undefined, FieldValueLabelUiOptions>,
): React.ReactElement {
  const uiOptions = props.uiSchema['ui:options'];
  const dependsOn = uiOptions?.dependsOn;
  const valuePath = uiOptions?.valuePath ?? '';
  const separator = uiOptions?.separator ?? DEFAULT_SEPARATOR;
  const emptyText = uiOptions?.emptyText ?? DEFAULT_EMPTY_TEXT;
  const direction = uiOptions?.direction ?? DEFAULT_DIRECTION;
  const title = stringSchemaField(props.schema, 'title');
  const description = stringSchemaField(props.schema, 'description');

  // Called unconditionally, before the required-field short-circuit below, so
  // the rules of hooks hold regardless of props.
  const state = useDependsOn(props, dependsOn);

  let body: React.ReactElement;

  if (props.required === true) {
    body = (
      <Text color="danger">
        FieldValueLabel cannot be required: it never receives a value
      </Text>
    );
  } else if (state.kind === 'author-error') {
    body = <Text color="danger">{state.message}</Text>;
  } else {
    const { items, warn } = resolveItems(
      state,
      dependsOn,
      valuePath,
      emptyText,
    );
    if (warn) {
      console.warn(
        `FieldValueLabel: valuePath "${valuePath}" resolved to an object or an array of objects; showing "${emptyText}" instead.`,
      );
    }
    body = renderBody(items, direction, separator);
  }

  return (
    <Flex direction="column">
      {title !== undefined && (
        <Text
          as="label"
          style={{
            color: 'var(--ns-color-fg-emphasis)',
            fontSize: 'var(--ns-font-size-s)',
            marginBottom: 'var(--ns-space-1)',
          }}
          weight="bold"
        >
          {title}
        </Text>
      )}
      {body}
      {description !== undefined && (
        <Text
          style={{
            color: 'var(--ns-color-fg-subtle)',
            fontSize: 'var(--ns-font-size-s)',
            marginTop: 'var(--ns-space-1)',
          }}
        >
          {description}
        </Text>
      )}
    </Flex>
  );
}

export const FieldValueLabel: FormField = createFormField({
  component: FieldValueLabelComponent,
  name: 'FieldValueLabel',
  schema: fieldValueLabelSchema,
});
